import { WorkflowSendingHours as AutomationSendingHours } from 'shared/types/graphql';
import React, { useEffect, useMemo } from 'react';
import { Flex } from '@televet/kibble-ui/build/chakra';
import { AutomationsList, AutomationEditor } from 'pages/Automations/components';
import { useGetAutomationEditorDataQuery } from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { updateAutomationDraft } from './state/automationsSlice';
import { useAppDispatch } from 'state/hooks';
import { convertAutomationPersistedToDraft } from './state/utils/draft';
import { useScrollToActiveAutomation } from 'pages/Automations/hooks/useScrollToActiveAutomation';
import { UnsavedAutomationChangesModal } from './components/Modals/UnsavedAutomationChangesModal';
import { DRAFT } from 'pages/Automations/hooks';
import { resetAutomationsState, setIsGlobalEditor } from 'pages/Automations/state/automationsSlice';
import { useCurrentAutomationId } from './hooks/useCurrentAutomationId';

export const formattedSendingHours = {
  [AutomationSendingHours.All24Hours]: '24 Hours',
  [AutomationSendingHours.AutomationHours]: 'Automation Hours',
  [AutomationSendingHours.BusinessHours]: 'Clinic Hours',
};

interface AutomationsProps {
  isGlobalEditor: boolean;
}

const Automations = ({ isGlobalEditor }: AutomationsProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const currentAutomationId = useCurrentAutomationId();
  const { scrollToActiveAutomation } = useScrollToActiveAutomation();

  useEffect(() => {
    dispatch(setIsGlobalEditor(isGlobalEditor));
  }, [isGlobalEditor, dispatch]);

  const isEmpty = useMemo(() => {
    return !currentAutomationId;
  }, [currentAutomationId]);

  const isNewDraft = useMemo(() => {
    return currentAutomationId?.includes(DRAFT);
  }, [currentAutomationId]);

  const { loading: loadingEditorData } = useGetAutomationEditorDataQuery({
    variables: { workflowEventSettingId: currentAutomationId },
    skip: isEmpty || isNewDraft,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    onCompleted: (data) => {
      if (data?.findUniqueWorkflowEventSetting && data.findUniqueWorkflowEventSetting.id === currentAutomationId) {
        const automation = convertAutomationPersistedToDraft({
          automation: data.findUniqueWorkflowEventSetting,
          conditionSets: data.findManyConditionSet,
        });
        dispatch(resetAutomationsState({ preserveAutomationDraft: true }));
        dispatch(updateAutomationDraft(automation));
      }
    },
  });

  useEffect(() => {
    if (isEmpty || isNewDraft) {
      dispatch(resetAutomationsState());
    }
  }, [isEmpty, isNewDraft, dispatch]);

  useEffect(() => {
    setTimeout(() => {
      scrollToActiveAutomation(currentAutomationId);
    }, 500);
  }, [currentAutomationId, scrollToActiveAutomation]);

  return (
    <Flex direction="row" className="Automations__Container" position="relative" overflow="hidden" height="100%">
      <AutomationsList />
      <AutomationEditor isEmpty={isEmpty} isLoading={loadingEditorData} />
      <UnsavedAutomationChangesModal />
    </Flex>
  );
};

export default Automations;
